import type {
  CustomMarker,
  CustomMarkersBySubEvent,
} from '../../views/following/components/custom-markers/types';
import type { Route } from '../../components/race-map/types';
import type { SegmentInfoWindowPropTypes } from '../../components/race-map/components/SegmentInfoWindow';

export type CustomMarkersActionPayload = {
  subEventId: number;
  customMarker: CustomMarker;
};

export type MapState = {
  customMarkers: CustomMarkersBySubEvent;
  map?: google.maps.Map;
  route?: Route;
  routeInfoWindow: 'none' | 'start' | 'finish';
  segmentInfoWindow?: SegmentInfoWindowPropTypes;
};

export type MapStateAction =
  | { type: 'setCustomMarkers'; payload: CustomMarkersBySubEvent }
  | { type: 'setCustomMarker'; payload: CustomMarkersActionPayload }
  | { type: 'deleteCustomMarker'; payload: CustomMarkersActionPayload }
  | { type: 'updateCustomMarker'; payload: CustomMarkersActionPayload }
  | { type: 'setMap'; payload: google.maps.Map | undefined }
  | { type: 'setRoute'; payload: Route | undefined }
  | { type: 'setRouteInfoWindow'; payload: 'none' | 'start' | 'finish' }
  | { type: 'setSegmentInfoWindow'; payload: SegmentInfoWindowPropTypes | undefined };

export const mapReducer = (state: MapState, action: MapStateAction): MapState => {
  const { type, payload } = action;

  switch (type) {
    case 'setCustomMarkers':
      return {
        ...state,
        customMarkers: { ...action.payload },
      };

    case 'setCustomMarker': {
      const { subEventId, customMarker } = payload;
      const customMarkersForSubEvent = state.customMarkers?.[subEventId] ?? [];
      return {
        ...state,
        customMarkers: {
          ...state.customMarkers,
          [subEventId]: [...customMarkersForSubEvent, customMarker],
        },
      };
    }

    case 'updateCustomMarker': {
      const { subEventId, customMarker } = action.payload;
      const customMarkersForSubEvent = state.customMarkers?.[subEventId]
        ? [...state.customMarkers[subEventId]]
        : [];
      const updateItemIndex = customMarkersForSubEvent.findIndex(
        (marker) => marker.id === customMarker.id
      );

      if (updateItemIndex < 0) return state;
      customMarkersForSubEvent[updateItemIndex] = customMarker;

      return {
        ...state,
        customMarkers: {
          ...state.customMarkers,
          [subEventId]: [...customMarkersForSubEvent],
        },
      };
    }

    case 'deleteCustomMarker': {
      const { subEventId, customMarker: customMarkerToDelete } = action.payload;
      const customMarkersForSubEvent = state.customMarkers?.[subEventId] ?? [];
      const updatedCustomMarkersForSubEvent = customMarkersForSubEvent.filter(
        (customMarker) => customMarker.id !== customMarkerToDelete.id
      );
      return {
        ...state,
        customMarkers: {
          ...state.customMarkers,
          [subEventId]: updatedCustomMarkersForSubEvent,
        },
      };
    }

    case 'setMap':
      return {
        ...state,
        map: payload,
        route: undefined,
      };

    case 'setRoute':
      return {
        ...state,
        route: payload,
      };

    case 'setRouteInfoWindow':
      return {
        ...state,
        routeInfoWindow: payload,
      };

    case 'setSegmentInfoWindow':
      return {
        ...state,
        segmentInfoWindow: payload,
      };

    default:
      return state;
  }
};
